import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gantt, ViewMode } from 'gantt-task-react';
import 'gantt-task-react/dist/index.css';
import { FiFilter, FiChevronDown, FiCheck, FiBarChart2 } from 'react-icons/fi';
import './PortfolioGantt.css';

const STATUS_COLORS = {
  'Not Started': '#B9B7AE',
  'In Progress': '#0F0F10',
  'Done': '#1DBF73',
  'Blocked': '#E24B5B',
};

const PortfolioGantt = ({ token }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ boards: [], groups: [], items: [] });
  const [viewMode, setViewMode] = useState(ViewMode.Week);
  const [selectedBoardIds, setSelectedBoardIds] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [boardPickerOpen, setBoardPickerOpen] = useState(false);
  const [hiddenBoardRows, setHiddenBoardRows] = useState({});

  useEffect(() => {
    fetchPortfolio();
  }, [statusFilter]); // eslint-disable-line

  const fetchPortfolio = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/portfolio/gantt?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`Failed to load portfolio (${res.status})`);
      const json = await res.json();
      setData({
        boards: json.boards || [],
        groups: json.groups || [],
        items: json.items || [],
      });
      setError(null);
    } catch (e) {
      console.error('Portfolio Gantt fetch error', e);
      setError(e.message || 'Unable to load portfolio data');
    } finally {
      setLoading(false);
    }
  };

  // Default: select all boards on first load
  useEffect(() => {
    if (data.boards.length > 0 && selectedBoardIds.length === 0) {
      setSelectedBoardIds(data.boards.map((b) => b.id));
    }
  }, [data.boards]); // eslint-disable-line

  const toggleBoard = (id) => {
    setSelectedBoardIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAllBoards = () => setSelectedBoardIds(data.boards.map((b) => b.id));
  const clearBoards = () => setSelectedBoardIds([]);

  const ganttTasks = useMemo(() => {
    const out = [];
    const selectedSet = new Set(selectedBoardIds);
    const visibleBoards = data.boards.filter((b) => selectedSet.has(b.id));

    visibleBoards.forEach((board) => {
      // Items belonging to this board
      const boardItems = data.items.filter((i) => i.boardId === board.id);

      // Determine project (board) span
      const starts = boardItems
        .filter((i) => i.startDate)
        .map((i) => new Date(i.startDate));
      const ends = boardItems
        .filter((i) => i.dueDate)
        .map((i) => new Date(i.dueDate));

      const today = new Date();
      const start =
        starts.length > 0 ? new Date(Math.min(...starts)) : today;
      const end =
        ends.length > 0
          ? new Date(Math.max(...ends))
          : new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

      const progress =
        boardItems.length > 0
          ? Math.round(
              boardItems.reduce((s, i) => s + (i.progress || 0), 0) /
                boardItems.length
            )
          : 0;

      const hideChildren = !!hiddenBoardRows[board.id];

      out.push({
        id: `board-${board.id}`,
        name: board.name,
        start,
        end,
        progress,
        type: 'project',
        hideChildren,
        styles: {
          backgroundColor: board.color || '#0F0F10',
          backgroundSelectedColor: board.color || '#0F0F10',
          progressColor: '#ffffff',
          progressSelectedColor: '#ffffff',
        },
        _meta: { kind: 'board', boardId: board.id },
      });

      boardItems.forEach((item) => {
        // Skip items without any date info to keep gantt clean
        if (!item.startDate && !item.dueDate) return;

        const itemStart = item.startDate
          ? new Date(item.startDate)
          : new Date();
        let itemEnd = item.dueDate
          ? new Date(item.dueDate)
          : new Date(itemStart.getTime() + 3 * 24 * 60 * 60 * 1000);
        if (itemEnd <= itemStart) {
          itemEnd = new Date(itemStart.getTime() + 24 * 60 * 60 * 1000);
        }

        const color = STATUS_COLORS[item.status] || '#0F0F10';
        out.push({
          id: item.id,
          name: item.name,
          start: itemStart,
          end: itemEnd,
          progress: item.progress || 0,
          type: 'task',
          project: `board-${board.id}`,
          isDisabled: false,
          styles: {
            backgroundColor: color,
            backgroundSelectedColor: color,
            progressColor: '#ffffff',
            progressSelectedColor: '#ffffff',
          },
          _meta: { kind: 'item', boardId: board.id, itemId: item.id },
        });
      });
    });

    return out;
  }, [data, selectedBoardIds, hiddenBoardRows]);

  const handleExpanderClick = (task) => {
    const meta = task._meta;
    if (meta && meta.kind === 'board') {
      setHiddenBoardRows((prev) => ({
        ...prev,
        [meta.boardId]: !prev[meta.boardId],
      }));
    }
  };

  const handleDoubleClick = (task) => {
    const meta = task._meta;
    if (!meta) return;
    if (meta.kind === 'item') {
      navigate(`/boards/${meta.boardId}?taskId=${meta.itemId}`);
    } else if (meta.kind === 'board') {
      navigate(`/boards/${meta.boardId}`);
    }
  };

  const handleSelect = (task) => {
    // Single-click is treated as preview/select only; navigation requires double-click.
    void task;
  };

  // Summary metrics
  const summary = useMemo(() => {
    const selectedSet = new Set(selectedBoardIds);
    const items = data.items.filter((i) => selectedSet.has(i.boardId));
    const total = items.length;
    const done = items.filter((i) => i.status === 'Done').length;
    const inProgress = items.filter((i) => i.status === 'In Progress').length;
    const blocked = items.filter((i) => i.status === 'Blocked').length;
    const overdue = items.filter((i) => {
      if (!i.dueDate || i.status === 'Done') return false;
      try {
        return new Date(i.dueDate) < new Date();
      } catch {
        return false;
      }
    }).length;
    return { total, done, inProgress, blocked, overdue };
  }, [data.items, selectedBoardIds]);

  return (
    <div className="portfolio-gantt-page" data-testid="portfolio-gantt-page">
      <div className="portfolio-header">
        <div>
          <div className="portfolio-title-row">
            <FiBarChart2 size={22} />
            <h1 className="portfolio-title">Portfolio Gantt</h1>
          </div>
          <div className="portfolio-subtitle">
            Cross-board timeline of all your projects
          </div>
        </div>
        <div className="portfolio-controls">
          <div className="portfolio-filter-group">
            <FiFilter size={14} />
            <select
              className="portfolio-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="portfolio-status-filter"
            >
              <option value="">All Statuses</option>
              <option value="Not Started">Not Started</option>
              <option value="In Progress">In Progress</option>
              <option value="Done">Done</option>
              <option value="Blocked">Blocked</option>
            </select>
          </div>

          <div className="portfolio-board-picker">
            <button
              className="portfolio-select board-picker-trigger"
              onClick={() => setBoardPickerOpen((v) => !v)}
              data-testid="portfolio-board-picker-button"
              type="button"
            >
              <span>
                Boards: {selectedBoardIds.length}/{data.boards.length}
              </span>
              <FiChevronDown size={14} />
            </button>
            {boardPickerOpen && (
              <div className="board-picker-menu" data-testid="board-picker-menu">
                <div className="board-picker-actions">
                  <button
                    type="button"
                    className="link-btn"
                    onClick={selectAllBoards}
                    data-testid="board-picker-select-all"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="link-btn"
                    onClick={clearBoards}
                    data-testid="board-picker-clear"
                  >
                    Clear
                  </button>
                </div>
                <div className="board-picker-list">
                  {data.boards.map((b) => {
                    const checked = selectedBoardIds.includes(b.id);
                    return (
                      <label
                        key={b.id}
                        className="board-picker-item"
                        data-testid="board-picker-item"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleBoard(b.id)}
                        />
                        <span
                          className="board-picker-color"
                          style={{ backgroundColor: b.color || '#0F0F10' }}
                        />
                        <span className="board-picker-name">{b.name}</span>
                        {checked && (
                          <FiCheck size={14} className="board-picker-check" />
                        )}
                      </label>
                    );
                  })}
                  {data.boards.length === 0 && (
                    <div className="board-picker-empty">No boards available</div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="gantt-view-switcher">
            <button
              type="button"
              className={`gantt-view-btn ${viewMode === ViewMode.Day ? 'active' : ''}`}
              onClick={() => setViewMode(ViewMode.Day)}
              data-testid="portfolio-view-day"
            >
              Day
            </button>
            <button
              type="button"
              className={`gantt-view-btn ${viewMode === ViewMode.Week ? 'active' : ''}`}
              onClick={() => setViewMode(ViewMode.Week)}
              data-testid="portfolio-view-week"
            >
              Week
            </button>
            <button
              type="button"
              className={`gantt-view-btn ${viewMode === ViewMode.Month ? 'active' : ''}`}
              onClick={() => setViewMode(ViewMode.Month)}
              data-testid="portfolio-view-month"
            >
              Month
            </button>
          </div>
        </div>
      </div>

      <div className="portfolio-summary" data-testid="portfolio-summary">
        <div className="summary-card">
          <div className="summary-label">Boards</div>
          <div className="summary-value">{selectedBoardIds.length}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Tasks</div>
          <div className="summary-value">{summary.total}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">In Progress</div>
          <div className="summary-value in-progress">{summary.inProgress}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Done</div>
          <div className="summary-value done">{summary.done}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Blocked</div>
          <div className="summary-value blocked">{summary.blocked}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Overdue</div>
          <div className="summary-value overdue">{summary.overdue}</div>
        </div>
      </div>

      <div className="portfolio-body">
        {loading ? (
          <div className="portfolio-loading" data-testid="portfolio-loading">
            <div className="loading-spinner" />
            <p>Loading portfolio…</p>
          </div>
        ) : error ? (
          <div className="portfolio-empty" data-testid="portfolio-error">
            <h3>Could not load portfolio</h3>
            <p>{error}</p>
            <button
              type="button"
              className="portfolio-retry-btn"
              onClick={fetchPortfolio}
              data-testid="portfolio-retry-button"
            >
              Try again
            </button>
          </div>
        ) : ganttTasks.length === 0 ? (
          <div className="portfolio-empty" data-testid="portfolio-empty">
            <h3>No tasks to show</h3>
            <p>
              {selectedBoardIds.length === 0
                ? 'Select at least one board to see its timeline.'
                : 'Selected boards have no tasks with start/due dates.'}
            </p>
          </div>
        ) : (
          <div className="portfolio-gantt-container" data-testid="portfolio-gantt-container">
            <Gantt
              tasks={ganttTasks}
              viewMode={viewMode}
              onExpanderClick={handleExpanderClick}
              onDoubleClick={handleDoubleClick}
              onSelect={handleSelect}
              listCellWidth="260px"
              columnWidth={
                viewMode === ViewMode.Month
                  ? 110
                  : viewMode === ViewMode.Week
                  ? 100
                  : 60
              }
              ganttHeight={Math.max(420, Math.min(720, ganttTasks.length * 38))}
              barBackgroundColor="#0F0F10"
              barBackgroundSelectedColor="#1A1A1C"
              barProgressColor="#ffffff"
              barProgressSelectedColor="#ffffff"
              projectBackgroundColor="#4F8DFF"
              projectBackgroundSelectedColor="#4a7bc8"
              projectProgressColor="#ffffff"
              projectProgressSelectedColor="#ffffff"
              arrowColor="#0F0F10"
              arrowIndent={20}
              fontSize="13px"
              fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
              todayColor="rgba(0, 115, 234, 0.12)"
              rowHeight={38}
              headerHeight={50}
            />
            <div className="portfolio-legend">
              <span className="legend-item">
                <span className="legend-swatch" style={{ background: '#B9B7AE' }} />
                Not Started
              </span>
              <span className="legend-item">
                <span className="legend-swatch" style={{ background: '#0F0F10' }} />
                In Progress
              </span>
              <span className="legend-item">
                <span className="legend-swatch" style={{ background: '#1DBF73' }} />
                Done
              </span>
              <span className="legend-item">
                <span className="legend-swatch" style={{ background: '#E24B5B' }} />
                Blocked
              </span>
              <span className="legend-hint">Double-click a bar to open it</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortfolioGantt;
